import { Inbox } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { useUploadedDocuments } from '@/features/emr';

interface InboxIconButtonProps {
  count: number;
  onClick: () => void;
}

/** "3" for 3, "9+" above 9, null (no badge) at zero or below. */
export function formatBadgeCount(count: number): string | null {
  if (count <= 0) return null;
  return count > 9 ? '9+' : String(count);
}

/** Presentational half of PersonalInboxButton, split out for tests. */
export function InboxIconButton({ count, onClick }: InboxIconButtonProps) {
  const badge = formatBadgeCount(count);
  const label = badge === null ? 'Open inbox' : `Open inbox, ${badge} unassigned`;
  return (
    <Button size="icon" variant="ghost" aria-label={label} className="relative h-9 w-9" onClick={onClick}>
      <Inbox className="h-5 w-5" />
      {badge !== null && (
        <span
          data-testid="inbox-badge"
          className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
          {badge}
        </span>
      )}
    </Button>
  );
}

/**
 * Personal-edition header shortcut to the document Inbox (/documents), with a
 * badge counting uploads not yet assigned to a household member. Count only —
 * never document names (PHI). If the count query fails, `data` stays
 * undefined, the badge hides, and the button still navigates.
 */
export function PersonalInboxButton() {
  const navigate = useNavigate();
  // limit: 1 — only `meta.total_items` is used, not the rows. Upload/assign
  // mutations invalidate the ['uploaded-documents', orgId] prefix, so the
  // count stays fresh without polling.
  const { data } = useUploadedDocuments({ limit: 1, inbox: true });
  const count = data?.meta.total_items ?? 0;

  return <InboxIconButton count={count} onClick={() => navigate({ to: '/documents' })} />;
}
